import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { recordPurchasePaymentRpc } from '@/services/purchases';
import { queryKeys } from '@/lib/queryKeys';
import { parseDatabaseError } from '@/lib/errors';
import { Purchase } from '@/types/models';
import { Modal } from '@/components/common/Modal';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { DollarSign, Loader2 } from 'lucide-react';

interface PurchasePaymentModalProps {
  purchase: Purchase;
  isOpen: boolean;
  onClose: () => void;
}

export const PurchasePaymentModal: React.FC<PurchasePaymentModalProps> = ({
  purchase,
  isOpen,
  onClose,
}) => {
  const queryClient = useQueryClient();
  const balanceDue = Math.max(0, purchase.total - (purchase.amount_paid || 0));

  const [amount, setAmount] = useState<number>(balanceDue);
  const [method, setMethod] = useState<string>('bank_transfer');
  const [notes, setNotes] = useState<string>('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async () => {
      if (amount <= 0) throw new Error('Payment amount must be greater than zero.');
      await recordPurchasePaymentRpc({
        purchase_id: purchase.id,
        amount,
        payment_method: method,
        notes: notes || undefined,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.purchases.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.purchases.detail(purchase.id) });
      onClose();
    },
    onError: (err: unknown) => {
      setErrorMsg(parseDatabaseError(err));
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);
    mutation.mutate();
  };

  return (
    <Modal isOpen={isOpen} title={`Record Supplier Payment — ${purchase.supplier?.name || 'Purchase'}`} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {errorMsg && <ErrorAlert message={errorMsg} />}

        <div className="bg-brand-bg p-3 rounded-lg border border-brand-border flex items-center justify-between text-xs">
          <div>
            <p className="font-semibold text-brand-muted uppercase">Total Invoice Amount</p>
            <p className="text-sm font-bold text-brand-black">Rs. {(purchase.total || 0).toLocaleString('en-PK')}</p>
          </div>
          <div>
            <p className="font-semibold text-brand-muted uppercase">Amount Paid</p>
            <p className="text-sm font-bold text-brand-green">Rs. {(purchase.amount_paid || 0).toLocaleString('en-PK')}</p>
          </div>
          <div>
            <p className="font-semibold text-brand-muted uppercase">Balance Due</p>
            <p className="text-sm font-bold text-brand-red">Rs. {balanceDue.toLocaleString('en-PK')}</p>
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-brand-black uppercase tracking-wider mb-1">
            Payment Amount (PKR) <span className="text-brand-red">*</span>
          </label>
          <input
            type="number"
            min={1}
            value={amount}
            onChange={(e) => setAmount(Number(e.target.value) || 0)}
            className="w-full px-3 py-2 bg-brand-bg border border-brand-border rounded-md text-sm text-brand-black focus:outline-none focus:ring-2 focus:ring-brand-green"
          />
        </div>

        <div>
          <label className="block text-xs font-semibold text-brand-black uppercase tracking-wider mb-1">
            Payment Method
          </label>
          <select
            value={method}
            onChange={(e) => setMethod(e.target.value)}
            className="w-full px-3 py-2 bg-brand-bg border border-brand-border rounded-md text-sm text-brand-black focus:outline-none focus:ring-2 focus:ring-brand-green"
          >
            <option value="bank_transfer">Bank Transfer</option>
            <option value="cash">Cash</option>
            <option value="mobile_wallet">Mobile Wallet</option>
            <option value="other">Other</option>
          </select>
        </div>

        <div>
          <label className="block text-xs font-semibold text-brand-black uppercase tracking-wider mb-1">
            Notes / Reference
          </label>
          <input
            type="text"
            placeholder="e.g. Bank Ref #TX99102"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="w-full px-3 py-2 bg-brand-bg border border-brand-border rounded-md text-sm text-brand-black focus:outline-none focus:ring-2 focus:ring-brand-green"
          />
        </div>

        <div className="flex items-center justify-end space-x-3 pt-3 border-t border-brand-border">
          <button type="button" onClick={onClose} disabled={mutation.isPending} className="px-4 py-2 text-sm font-medium text-brand-muted hover:text-brand-black transition-colors">
            Cancel
          </button>
          <button
            type="submit"
            disabled={mutation.isPending || amount <= 0}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-brand-green text-white text-sm font-semibold rounded-md hover:bg-brand-green-hover transition-colors disabled:opacity-50"
          >
            {mutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <DollarSign className="w-4 h-4" />}
            <span>Record Payment</span>
          </button>
        </div>
      </form>
    </Modal>
  );
};
